package br.com.vvrefeicoes.portal.feature.operacional

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.di.LoadErrorState
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.di.loadGuarded
import br.com.vvrefeicoes.portal.core.util.DateTimeProvider
import br.com.vvrefeicoes.portal.core.util.SystemDateTimeProvider
import br.com.vvrefeicoes.portal.domain.model.ComplaintStatus
import br.com.vvrefeicoes.portal.domain.model.CustomerComplaint
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.OrgChartData
import br.com.vvrefeicoes.portal.domain.model.responsibleNames
import br.com.vvrefeicoes.portal.domain.repository.CustomerComplaintRepository
import br.com.vvrefeicoes.portal.domain.repository.OrgChartRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import kotlin.random.Random

data class ComplaintForm(
    val date: String = "",
    val time: String = "",
    val directorate: String = "",
    val managerName: String = "",
    val restaurant: String = "",
    val service: String = "",
    val status: ComplaintStatus = ComplaintStatus.OPEN,
    val supplier: String = "",
    val shift: String = "",
    val nature: String = "",
    val description: String = "",
    val treatment: String = "",
    val treatmentOwner: String = "",
)

class CustomerComplaintController(
    private val repository: CustomerComplaintRepository,
    private val orgChartRepository: OrgChartRepository,
    private val scope: CoroutineScope,
    private val dateTimeProvider: DateTimeProvider = SystemDateTimeProvider(),
    private val loadError: LoadErrorState = LoadErrorState(),
) {
    var loading by mutableStateOf(true)
        private set

    var records by mutableStateOf<List<CustomerComplaint>>(emptyList())
        private set

    var period by mutableStateOf(dateTimeProvider.currentPeriod())
        private set

    var directorOptions by mutableStateOf<List<String>>(emptyList())
        private set

    var managerOptions by mutableStateOf<List<String>>(emptyList())
        private set

    private var started = false
    private var unitName: String? = null
    private var orgChartData: OrgChartData? = null

    fun init() {
        if (started) return
        started = true
        scope.launch {
            loadError.loadGuarded {
                orgChartData = orgChartRepository.loadInitial().currentVersion.data
                applyResponsibleNames()
                load()
            }
            loading = false
        }
    }

    fun applyUnit(
        name: String,
        authorizedNames: Set<String>,
    ) {
        val resolved = name.takeIf { it.isNotBlank() && it in authorizedNames }.orEmpty()
        if (unitName == resolved) return
        unitName = resolved
        applyResponsibleNames()
        if (started) scope.launch { loadError.loadGuarded { load() } }
    }

    fun applyPeriod(month: MonthPeriod) {
        if (period == month) return
        period = month
        if (started) scope.launch { loadError.loadGuarded { load() } }
    }

    var formOpen by mutableStateOf(false)
        private set

    var formRecordId by mutableStateOf<String?>(null)
        private set

    var form by mutableStateOf(ComplaintForm())
        private set

    var saving by mutableStateOf(false)
        private set

    var deleteTarget by mutableStateOf<CustomerComplaint?>(null)
        private set

    var deleting by mutableStateOf(false)
        private set

    private var dateBlurred by mutableStateOf(false)
    private var timeBlurred by mutableStateOf(false)

    val canSubmitForm: Boolean
        get() =
            !saving &&
                parseDateIso(maskDate(form.date)) != null &&
                isValidTime(maskTime(form.time)) &&
                form.nature.isNotBlank() &&
                form.description.isNotBlank()

    val dateError: Boolean
        get() = dateBlurred && parseDateIso(maskDate(form.date)) == null

    val timeError: Boolean
        get() = timeBlurred && !isValidTime(maskTime(form.time))

    fun openAdd() {
        form =
            ComplaintForm(
                directorate = directorOptions.firstOrNull().orEmpty(),
                managerName = managerOptions.firstOrNull().orEmpty(),
                restaurant = unitName.orEmpty(),
            )
        dateBlurred = false
        timeBlurred = false
        formRecordId = null
        formOpen = true
    }

    fun openEdit(record: CustomerComplaint) {
        form =
            ComplaintForm(
                date = isoToBr(record.date),
                time = record.time,
                directorate = record.directorate,
                managerName = record.managerName,
                restaurant = record.restaurant,
                service = record.service,
                status = record.status,
                supplier = record.supplier,
                shift = record.shift,
                nature = record.nature,
                description = record.description,
                treatment = record.treatment,
                treatmentOwner = record.treatmentOwner,
            )
        dateBlurred = false
        timeBlurred = false
        formRecordId = record.id
        formOpen = true
    }

    fun dismissForm() {
        formOpen = false
        formRecordId = null
    }

    fun updateForm(transform: (ComplaintForm) -> ComplaintForm) {
        form = transform(form)
    }

    fun updateDate(value: String) {
        dateBlurred = false
        form = form.copy(date = value.take(10))
    }

    fun updateTime(value: String) {
        timeBlurred = false
        form = form.copy(time = value.take(5))
    }

    fun commitDate() {
        dateBlurred = true
        form = form.copy(date = maskDate(form.date))
    }

    fun commitTime() {
        timeBlurred = true
        form = form.copy(time = maskTime(form.time))
    }

    fun submitForm() {
        val isoDate = parseDateIso(maskDate(form.date)) ?: return
        if (!canSubmitForm) return
        val current = form
        val record =
            CustomerComplaint(
                id = formRecordId ?: "cmp-${Random.nextInt(1_000_000)}",
                date = isoDate,
                time = maskTime(current.time),
                directorate = current.directorate.trim(),
                managerName = current.managerName.trim(),
                restaurant = current.restaurant.trim(),
                service = current.service.trim(),
                status = current.status,
                supplier = current.supplier.trim(),
                shift = current.shift.trim(),
                nature = current.nature.trim(),
                description = current.description.trim(),
                treatment = current.treatment.trim(),
                treatmentOwner = current.treatmentOwner.trim(),
                unidadeId = unidadeIdFor(unitName),
            )
        saving = true
        scope.launch {
            try {
                repository.upsert(record)
                load()
                formOpen = false
                formRecordId = null
            } finally {
                saving = false
            }
        }
    }

    fun requestDelete(record: CustomerComplaint) {
        deleteTarget = record
    }

    fun dismissDelete() {
        if (deleting) return
        deleteTarget = null
    }

    fun confirmDelete() {
        val target = deleteTarget ?: return
        if (deleting) return
        deleting = true
        scope.launch {
            try {
                repository.delete(target.id)
                load()
                deleteTarget = null
            } finally {
                deleting = false
            }
        }
    }

    private fun applyResponsibleNames() {
        val names = orgChartData?.responsibleNames(unitName.orEmpty())
        directorOptions = names?.first.orEmpty()
        managerOptions = names?.second.orEmpty()
    }

    private suspend fun load() {
        val unitId = unidadeIdFor(unitName)
        records =
            if (unitName.isNullOrBlank()) {
                emptyList()
            } else {
                repository.load(period).filter { record ->
                    if (unitId.isNotBlank()) {
                        record.unidadeId == unitId
                    } else {
                        record.restaurant.equals(unitName, ignoreCase = true)
                    }
                }
            }
    }

    private fun unidadeIdFor(name: String?): String {
        if (name.isNullOrBlank()) return ""
        val company =
            orgChartData?.companies?.firstOrNull { !it.inactive && it.name.equals(name, ignoreCase = true) }
                ?: return ""
        return company.organogramaUnidadeId.ifBlank { company.id }
    }
}

@Composable
fun rememberCustomerComplaintController(): CustomerComplaintController {
    val appGraph = LocalAppGraph.current
    val scope = rememberCoroutineScope()
    return remember {
        CustomerComplaintController(
            repository = appGraph.customerComplaintRepository,
            orgChartRepository = appGraph.orgChartRepository,
            scope = scope,
            dateTimeProvider = appGraph.dateTimeProvider,
            loadError = appGraph.loadError,
        )
    }
}

private fun isoToBr(iso: String): String =
    if (iso.length >= 10) {
        "${iso.substring(8, 10)}/${iso.substring(5, 7)}/${iso.substring(0, 4)}"
    } else {
        iso
    }

private fun parseDateIso(value: String): String? {
    val parts = value.trim().split("/")
    if (parts.size != 3) return null
    val day = parts[0].toIntOrNull() ?: return null
    val month = parts[1].toIntOrNull() ?: return null
    val year = parts[2].toIntOrNull() ?: return null
    if (day !in 1..31 || month !in 1..12 || year !in 1900..2100) return null
    return "$year-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}"
}

private fun maskDate(value: String): String {
    val digits = value.filter { it.isDigit() }.take(8)
    return buildString {
        digits.forEachIndexed { index, digit ->
            if (index == 2 || index == 4) append('/')
            append(digit)
        }
    }
}

private fun maskTime(value: String): String {
    val digits = value.filter { it.isDigit() }.take(4)
    return if (digits.length <= 2) digits else digits.substring(0, 2) + ":" + digits.substring(2)
}

private fun isValidTime(value: String): Boolean {
    val parts = value.trim().split(":")
    if (parts.size != 2 || parts[0].length != 2 || parts[1].length != 2) return false
    val hour = parts[0].toIntOrNull() ?: return false
    val minute = parts[1].toIntOrNull() ?: return false
    return hour in 0..23 && minute in 0..59
}
